import { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import SEOMeta from '../../components/SEOMeta';
import { useDarkMode } from '../../hooks/useDarkMode';

export default function OuvirPage() {
  const isDark = useDarkMode();
  const [searchParams] = useSearchParams();
  const audioUrl = searchParams.get('url') || '';
  const nome    = searchParams.get('nome') || 'você';
  const titulo  = searchParams.get('titulo') || `Música para ${nome}`;
  const letra   = searchParams.get('letra') || '';
  const imagem  = searchParams.get('imagem') || '';

  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [playError, setPlayError] = useState(false);
  const [letraAberta, setLetraAberta] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onEnded = () => setPlaying(false);
    audio.addEventListener('ended', onEnded);
    return () => audio.removeEventListener('ended', onEnded);
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.play()
        .then(() => setPlaying(true))
        .catch(() => setPlayError(true));
    }
  };

  const shareText = encodeURIComponent(
    '🎵 Ouça a música personalizada criada com carinho para ' + nome + '!\n\n' +
    window.location.href +
    '\n\nGostou? Faça sua homenagem pra alguém agora mesmo: inspiramusicas.com.br'
  );

  if (!audioUrl) {
    return (
      <div style={{ background: 'var(--cream)', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <p style={{ color: 'var(--text-muted)', fontFamily: "'DM Sans', sans-serif" }}>Link inválido ou expirado.</p>
      </div>
    );
  }

  return (
    <>
      <SEOMeta
        title={`${titulo} | Inspira Músicas`}
        description={`Ouça a música personalizada: ${titulo}`}
        path="/ouvir"
        noIndex
      />
      <div style={{ background: 'var(--cream)', minHeight: '100vh', fontFamily: "'DM Sans', sans-serif" }}>
        {/* Header */}
        <header style={{ background: 'var(--warm-white)', borderBottom: '1px solid var(--beige-mid)', padding: '14px 24px', textAlign: 'center' }}>
          <Link to="/">
            <img src={isDark ? '/logo-dark.png' : '/logo-light.png'} alt="Inspira Músicas" style={{ height: 44 }} />
          </Link>
        </header>

        {/* Main */}
        <main style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '40px 16px 60px', minHeight: 'calc(100vh - 73px)' }}>
          <div style={{
            background: 'var(--warm-white)',
            border: '1px solid var(--border-soft)',
            borderRadius: 24,
            padding: '40px 32px',
            maxWidth: 440,
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 8px 40px rgba(200,131,42,0.10)',
          }}>

            {/* Imagem (capa) */}
            {imagem ? (
              <div style={{ marginBottom: 24 }}>
                <img
                  src={imagem}
                  alt={titulo}
                  style={{ width: '100%', aspectRatio: '1/1', objectFit: 'cover', borderRadius: 16, boxShadow: '0 4px 20px rgba(74,55,40,0.15)' }}
                />
              </div>
            ) : (
              /* Botão play grande (quando não há imagem) */
              <button
                onClick={togglePlay}
                aria-label={playing ? 'Pausar' : 'Tocar música'}
                style={{
                  width: 88, height: 88, borderRadius: '50%',
                  background: 'linear-gradient(135deg, var(--gold), var(--gold-light))',
                  border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 24px',
                  boxShadow: '0 6px 28px rgba(200,131,42,0.35)',
                  transition: 'transform 0.15s, box-shadow 0.15s',
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.05)';
                  (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 8px 36px rgba(200,131,42,0.45)';
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)';
                  (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 6px 28px rgba(200,131,42,0.35)';
                }}
              >
                {playing ? (
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <rect x="7" y="6" width="5" height="20" rx="2.5" fill="white" />
                    <rect x="20" y="6" width="5" height="20" rx="2.5" fill="white" />
                  </svg>
                ) : (
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <path d="M10 6L26 16L10 26V6Z" fill="white" />
                  </svg>
                )}
              </button>
            )}

            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, color: 'var(--earth)', margin: '0 0 8px', lineHeight: 1.3 }}>
              {titulo}
            </h1>
            <p style={{ color: 'var(--text-muted)', fontSize: 15, margin: '0 0 28px', lineHeight: 1.6 }}>
              Composta com carinho, exclusivamente para esta pessoa especial
            </p>

            {/* Native audio player */}
            <audio
              ref={audioRef}
              src={audioUrl}
              controls
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              style={{ width: '100%', marginBottom: 8, borderRadius: 8, accentColor: 'var(--gold)' }}
            />

            {playError && (
              <p style={{ color: 'var(--amber-deep)', fontSize: 13, margin: '8px 0 0' }}>
                Use o player acima ou{' '}
                <a href={audioUrl} style={{ color: 'var(--gold)', fontWeight: 700 }}>baixe o arquivo</a>.
              </p>
            )}

            {/* Letra */}
            {letra && (
              <>
                <div style={{ height: 1, background: 'linear-gradient(90deg, transparent, var(--border-fade), transparent)', margin: '24px 0' }} />
                <button
                  onClick={() => setLetraAberta(v => !v)}
                  style={{
                    width: '100%', background: 'transparent', border: '1.5px solid rgba(200,131,42,0.30)',
                    borderRadius: 12, padding: '10px 16px', cursor: 'pointer',
                    fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600,
                    color: 'var(--gold-hover)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}
                >
                  <span>{letraAberta ? '▲' : '▼'}</span>
                  {letraAberta ? 'Ocultar letra' : 'Ver letra da música'}
                </button>
                {letraAberta && (
                  <div style={{
                    marginTop: 16, textAlign: 'left', background: 'var(--cream)',
                    borderRadius: 12, padding: '20px 20px',
                    border: '1px solid rgba(200,131,42,0.12)',
                    maxHeight: 340, overflowY: 'auto',
                  }}>
                    <pre style={{
                      fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: 'var(--text-body)',
                      lineHeight: 1.8, whiteSpace: 'pre-wrap', margin: 0,
                    }}>
                      {letra}
                    </pre>
                  </div>
                )}
              </>
            )}

            <div style={{ height: 1, background: 'linear-gradient(90deg, transparent, var(--border-fade), transparent)', margin: '24px 0' }} />

            {/* WhatsApp share */}
            <a
              href={`https://api.whatsapp.com/send?text=${shareText}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                background: '#25D366', color: 'white', textDecoration: 'none',
                borderRadius: 999, padding: '13px 28px', fontSize: 15, fontWeight: 700,
                width: '100%', boxSizing: 'border-box',
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              Compartilhar no WhatsApp
            </a>

            <p style={{ fontSize: 12, color: 'var(--text-soft)', marginTop: 20, lineHeight: 1.6 }}>
              Feito com ♥ pela{' '}
              <a href="https://inspiramusicas.com.br" style={{ color: 'var(--amber-deep)' }}>Inspira Músicas</a>
            </p>
          </div>
        </main>
      </div>
    </>
  );
}
